import { QueryReturnValue } from '@reduxjs/toolkit/query'
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, createApi } from '@reduxjs/toolkit/query/react'

import { baseQuery } from '@/core/api/baseQuery'
import { GenericResponseOData } from '@/core/api/interfaces/GenericResponseOData'
import {
  AgenteOperativo,
  AsignacionEjecutivoOperativo,
  EjecutivoComercial,
} from '@/core/odata/interfaces/MtPerseveranceBackendApiRepositoriesModel'

export interface EjecutivoComercialAsignacion extends Pick<
  EjecutivoComercial,
  | 'EjecutivoComercialId'
  | 'Nombre'
  | 'Apellido'
  | 'CorreoElectronico'
  | 'Activo'
  | 'Identificador'
  | 'Iniciales'
  | 'NombreCompleto'
> {
  AsignacionEjecutivoOperativo?: Array<
    Pick<
      AsignacionEjecutivoOperativo,
      'AsignacionEjecutivoOperativoId' | 'FechaUltimaModificacion' | 'UsuarioUltimaModificacion'
    > & {
      AgenteOperativo?: Pick<AgenteOperativo, 'AgenteOperativoId' | 'Nombre' | 'Apellido' | 'Activo'>
    }
  >
  CantidadAsignaciones?: number
}

export interface AsignarAgenteOperativoPayload {
  EjecutivoComercialId: number
  AgenteOperativoId: number
}

// Solo los campos editables. El business endpoint carga la fila real y toca únicamente esto,
// así que ya no hay que reenviar Identificador/NombreCompleto/UsuarioId «tal cual» ni mandar
// UsuarioCreacion/UsuarioUltimaModificacion como placeholder vacío para pasar la validación
// de modelo, que era lo que exigía el PUT de OData sobre la entidad completa.
export interface ActualizarEjecutivoComercialPayload {
  EjecutivoComercialId: number
  Nombre: string
  Apellido: string
  CorreoElectronico: string
  Iniciales?: string | null
  Activo: boolean
}

// `Iniciales` salió del alta: el formulario ya no la pide y el backend dejó de exigirla.
export interface CrearEjecutivoPayload {
  Tipo: string
  Nombre: string
  Apellido: string
  Email: string
  Cargo?: string
}

export interface AgenteOperativoRegistro extends Pick<
  AgenteOperativo,
  'AgenteOperativoId' | 'UsuarioId' | 'Nombre' | 'Apellido' | 'Email' | 'Cargo' | 'Activo'
> {
  NombreCompleto: string
  FechaUltimaModificacion: string
  UsuarioUltimaModificacion: string
}

// Idem: solo campos editables.
export interface ActualizarAgenteOperativoPayload {
  AgenteOperativoId: number
  Nombre: string
  Apellido: string
  Email: string
  Cargo: string
  Activo: boolean
}

/**
 * Tope de vueltas, para que un servidor que responda cualquier cosa no deje al navegador pidiendo
 * páginas para siempre. Con el PageSize de estos endpoints (50) son 5.000 filas; ningún catálogo
 * de ejecutivos se acerca.
 */
const TOPE_TANDAS = 100

/**
 * La URL de la tanda siguiente.
 *
 * Se toma **solo la query** del `@odata.nextLink` y se pega a la ruta relativa original. El
 * nextLink viene absoluto con el host que vio el servidor (`https://localhost:50869/v1/...`), y
 * `baseQuery` resuelve su BASE_URL en tiempo de request para que un mismo bundle sirva EC/PE y
 * QA/UAT/PROD: usarlo tal cual saltaría esa resolución. Mismo razonamiento que
 * `helpers/consultaExportacion`, que resuelve esto para las exportaciones.
 */
const siguienteTanda = (consulta: string, nextLink: string | undefined, traidas: number): string => {
  const finRecurso = consulta.indexOf('?')
  const inicioQuery = nextLink === undefined ? -1 : nextLink.indexOf('?')

  if (nextLink !== undefined && inicioQuery !== -1 && finRecurso !== -1) {
    return `${consulta.slice(0, finRecurso)}${nextLink.slice(inicioQuery)}`
  }

  return `${consulta}${/[?&]$/.test(consulta) ? '' : '&'}$skip=${traidas}`
}

type RespuestaPaginada<T> = GenericResponseOData<T[]> & { '@odata.nextLink'?: string }

/**
 * La misma firma que RTK Query le pasa al cuarto parámetro de `queryFn`. El retorno es
 * `T | PromiseLike<T>` (el `MaybePromise` interno de la librería, que no exporta), por eso el `await`
 * del bucle: sirve para ambos casos.
 */
type FetchConBaseQuery = (
  args: string | FetchArgs,
) =>
  | QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
  | PromiseLike<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>

/**
 * Trae TODAS las filas siguiendo el `@odata.nextLink`.
 *
 * Los dos GET del módulo declaran `[EnableQuery(PageSize = 50)]`, que activa el paginado del lado
 * del servidor: aunque nadie pida un tope, la respuesta trae como mucho 50 filas más un
 * `@odata.nextLink`. Una sola petición se quedaba con las primeras 50 y la grilla mostraba ese
 * recorte como si fuera el catálogo completo, sin ningún aviso — el `take: 50` del grid es
 * paginado en cliente y no tiene nada que ver.
 *
 * Va por `queryFn` y no por `query` para poder encadenar peticiones conservando el `baseQuery`:
 * token, reintento del 401 y el `cache: 'no-store'` de estas lecturas.
 *
 * Las tandas van en serie: la URL de cada una sale de la respuesta de la anterior, así que no hay
 * nada que paralelizar. El paginado es estable porque `EnableQuery` ordena por la clave de la
 * entidad cuando hay `$skip` (`EnsureStableOrdering`, activo por omisión).
 */
const traerTodasLasPaginas = async <T>(
  consulta: string,
  fetchConBaseQuery: FetchConBaseQuery,
): Promise<QueryReturnValue<GenericResponseOData<T[]>, FetchBaseQueryError, FetchBaseQueryMeta | undefined>> => {
  const filas: T[] = []
  let siguiente = consulta

  for (let tanda = 0; tanda < TOPE_TANDAS; tanda++) {
    // eslint-disable-next-line no-await-in-loop
    const respuesta = await fetchConBaseQuery({ url: siguiente, method: 'GET', cache: 'no-store' })

    if (respuesta.error) {
      return { error: respuesta.error }
    }

    const cuerpo = respuesta.data as RespuestaPaginada<T> | undefined
    const lote = cuerpo?.value ?? []

    filas.push(...lote)

    // Una tanda vacía significa que no hay más (o que algo salió mal). Sin este corte, un servidor
    // que devuelva `[]` con un nextLink colgado dejaría el bucle girando.
    if (lote.length === 0 || cuerpo?.['@odata.nextLink'] === undefined) {
      break
    }

    siguiente = siguienteTanda(consulta, cuerpo['@odata.nextLink'], filas.length)
  }

  return { data: { '@odata.count': String(filas.length), value: filas } }
}

export const asignacionEjecutivosComercialesApi = createApi({
  reducerPath: 'asignacionEjecutivosComercialesApi',
  baseQuery,
  endpoints: (builder) => ({
    getEjecutivosComerciales: builder.query<GenericResponseOData<EjecutivoComercialAsignacion[]>, object>({
      // `cache: 'no-store'` (dentro de traerTodasLasPaginas): el GET viaja con
      // `Cache-Control: private, max-age=600` (CatalogoOutputCachePolicy), pensado para catalogos
      // que se leen mucho y cambian poco. Esta es la pantalla que los MODIFICA, asi que sin eso el
      // navegador seguia sirviendo su copia hasta 10 minutos y los cambios recien guardados no
      // aparecian aunque el servidor ya hubiera invalidado su cache.
      queryFn: (_arg, _api, _extra, fetchConBaseQuery) =>
        traerTodasLasPaginas<EjecutivoComercialAsignacion>(
          '/EjecutivosComercial?$select=EjecutivoComercialId,Nombre,Apellido,CorreoElectronico,Activo,Identificador,Iniciales,NombreCompleto&$expand=AsignacionEjecutivoOperativo($select=AsignacionEjecutivoOperativoId,FechaUltimaModificacion,UsuarioUltimaModificacion;$expand=AgenteOperativo($select=AgenteOperativoId,Nombre,Apellido,Activo))&$orderby=Nombre',
          fetchConBaseQuery,
        ),
    }),
    actualizarEjecutivoComercial: builder.mutation<void, ActualizarEjecutivoComercialPayload>({
      query: ({ EjecutivoComercialId, ...body }) => ({
        url: `/EjecutivosBusiness/EjecutivoComercial/${EjecutivoComercialId}`,
        method: 'PUT',
        body,
      }),
    }),
    // Asigna o reasigna, según lo que decida el backend: si el comercial ya tenía asignación la
    // reutiliza, si no la crea. Antes eran dos mutaciones y el front elegía POST o PUT según
    // tuviera a mano el id de la asignación; si no lo tenía (por ejemplo tras refrescar) creaba
    // una fila nueva que chocaba contra el índice único Idx_AsignacionEjecutivoOperativo.
    // No confundir con AsignacionAgenteOperativoBusiness (Booking/useUpdateAgenteOperativo),
    // que reasigna el agente operativo de un Booking puntual — es un concepto distinto.
    asignarAgenteOperativo: builder.mutation<void, AsignarAgenteOperativoPayload>({
      query: (payload) => ({
        url: '/EjecutivosBusiness/Asignacion',
        method: 'PUT',
        body: payload,
      }),
    }),
    // Envía los datos del mini formulario (Tipo, Nombre, Apellido, Email, Iniciales) al
    // business endpoint. La lógica de creación real (EjecutivoComercial vs AgenteOperativo
    // según Tipo) vive en el backend (EjecutivoBusinessController -> EjecutivoSupervisor).
    crearEjecutivo: builder.mutation<void, CrearEjecutivoPayload>({
      query: (payload) => ({
        url: '/EjecutivosBusiness/CrearEjecutivo',
        method: 'POST',
        body: payload,
      }),
    }),
    // CRUD directo sobre AgenteOperativo (pestaña "Agentes Operativos"). La creación de
    // agentes NUEVOS sigue yendo por crearEjecutivo (Tipo=Operativo) porque ese flujo
    // resuelve/crea el Usuario asociado; este endpoint es solo para editar/eliminar
    // agentes que ya existen (y por lo tanto ya tienen su Usuario vinculado).
    getAgentesOperativos: builder.query<GenericResponseOData<AgenteOperativoRegistro[]>, void>({
      // Mismo motivo que arriba, y mismo paginado del servidor.
      queryFn: (_arg, _api, _extra, fetchConBaseQuery) =>
        traerTodasLasPaginas<AgenteOperativoRegistro>(
          '/AgenteOperativos?$select=AgenteOperativoId,UsuarioId,Nombre,Apellido,Email,Cargo,NombreCompleto,Activo,FechaUltimaModificacion,UsuarioUltimaModificacion&$orderby=Nombre',
          fetchConBaseQuery,
        ),
    }),
    actualizarAgenteOperativo: builder.mutation<void, ActualizarAgenteOperativoPayload>({
      query: ({ AgenteOperativoId, ...body }) => ({
        url: `/EjecutivosBusiness/AgenteOperativo/${AgenteOperativoId}`,
        method: 'PUT',
        body,
      }),
    }),
    // Borrado FISICO, vía business endpoint. El DELETE de OData hacía borrado lógico con el
    // campo Eliminado (que en este repo no debe usarse) y dejaba al agente invisible pero vivo.
    // El business valida primero que no lo referencie ningún booking, actividad maestra ni
    // ejecutivo comercial: si lo hace, responde 400 con el detalle de qué lo bloquea.
    eliminarAgenteOperativo: builder.mutation<void, number>({
      query: (agenteOperativoId) => ({
        url: `/EjecutivosBusiness/AgenteOperativo/${agenteOperativoId}`,
        method: 'DELETE',
      }),
    }),
    // Mismo criterio, salvo que las asignaciones a agente operativo NO bloquean: son la
    // relación en sí, no un dato de negocio, y el backend las borra junto con el comercial.
    eliminarEjecutivoComercial: builder.mutation<void, number>({
      query: (ejecutivoComercialId) => ({
        url: `/EjecutivosBusiness/EjecutivoComercial/${ejecutivoComercialId}`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useLazyGetEjecutivosComercialesQuery,
  useAsignarAgenteOperativoMutation,
  useActualizarEjecutivoComercialMutation,
  useCrearEjecutivoMutation,
  useLazyGetAgentesOperativosQuery,
  useActualizarAgenteOperativoMutation,
  useEliminarAgenteOperativoMutation,
  useEliminarEjecutivoComercialMutation,
} = asignacionEjecutivosComercialesApi
